import { editingItemAtom } from "@/atoms"
import { fetchApi } from "@/lib"
import { ApiData } from "@/types"
import { useAtomValue } from "jotai"
import { useCallback, useEffect, useRef } from "react"

const STALE_THRESHOLD_MS = 30_000
const POLL_INTERVAL_MS = 60_000

/**
 * Module-level handle to the latest `refetch` function so any component
 * (e.g. checkout post-success) can force an immediate refresh of the API
 * data without waiting for the next 60s polling tick. Useful right after
 * a checkout: the merchant might have added a new service area or item
 * since the page was loaded, and the next cart flow should reflect it.
 */
let apiRefetchRef: (() => Promise<ApiData | null>) | null = null

/**
 * Triggers an immediate refetch of the API data (catalog, services,
 * service areas, etc.) and resolves with the fresh data so callers that need
 * to act on it right away (e.g. the checkout freshness revalidation) can read
 * it without waiting for a re-render to flush the atoms. Resolves with `null`
 * if `useApiPolling` is not mounted yet or the fetch fails.
 */
export function refreshApiData(): Promise<ApiData | null> {
  if (apiRefetchRef) {
    return apiRefetchRef()
  }
  return Promise.resolve(null)
}

export function useApiPolling(
  setData: (data: ApiData | null) => void,
  enabled: boolean,
) {
  const lastFetchRef = useRef(Date.now())
  const isEditingItem = useAtomValue(editingItemAtom)
  const isEditingRef = useRef(isEditingItem)
  isEditingRef.current = isEditingItem

  const refetch = useCallback(async (): Promise<ApiData | null> => {
    try {
      const apiData = await fetchApi()
      if (apiData) {
        setData(apiData)
        lastFetchRef.current = Date.now()
      }
      return apiData
    } catch {
      return null
    }
  }, [setData])

  useEffect(() => {
    apiRefetchRef = refetch
    return () => {
      if (apiRefetchRef === refetch) apiRefetchRef = null
    }
  }, [refetch])

  useEffect(() => {
    if (!isEditingItem && enabled) {
      const elapsed = Date.now() - lastFetchRef.current
      if (elapsed >= STALE_THRESHOLD_MS) {
        refetch()
      }
    }
  }, [isEditingItem, enabled, refetch])

  useEffect(() => {
    if (!enabled) return

    function handleVisibilityChange() {
      if (isEditingRef.current) return
      if (document.visibilityState === "visible") {
        const elapsed = Date.now() - lastFetchRef.current
        if (elapsed >= STALE_THRESHOLD_MS) {
          refetch()
        }
      }
    }

    document.addEventListener("visibilitychange", handleVisibilityChange)
    return () => document.removeEventListener("visibilitychange", handleVisibilityChange)
  }, [enabled, refetch])

  useEffect(() => {
    if (!enabled) return

    const intervalId = window.setInterval(() => {
      if (isEditingRef.current) return
      if (document.visibilityState !== "visible") return
      refetch()
    }, POLL_INTERVAL_MS)

    return () => window.clearInterval(intervalId)
  }, [enabled, refetch])
}
